import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { adsApiCall } from "./api";
import {
  Sparkles, Layers, Type, Image as ImageIcon, Download,
  Wand2, Rocket, Check, Zap, Palette, Globe,
} from "lucide-react";
import LivePreview from "./LivePreview";
import BeforeAfterSection from "./BeforeAfterSection";
import ReelDirectorBar from "./ReelDirectorBar";
import DirectorShowcase from "./DirectorShowcase";
import AchievementCelebration from "./AchievementCelebration";
import { StudioShell } from "../studio/StudioShell";
import { useLanguage } from "../contexts/LanguageContext";
import "./ads-creator.css";

export default function AdsCreatorLanding() {
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [packages, setPackages] = useState([]);
  // When a showcase card is clicked, we pre-fill the director bar with the
  // showcase's prompt and scroll to it. ReelDirectorBar watches this prop.
  const [presetPrompt, setPresetPrompt] = useState("");

  useEffect(() => {
    adsApiCall.publicStats().then(setStats).catch(() => {});
    adsApiCall.meta().then((m) => setPackages(m.packages)).catch(() => {});
  }, []);

  return (
    <StudioShell activeKey="/ads-creator">
    <div className="ads-root" data-testid="ads-creator-landing">
      <Helmet>
        <title>{t.studio_landing_meta_title || "Studio — AI designs, reels & videos in 60 seconds | TopRankPulse"}</title>
        <meta name="description" content={t.studio_landing_meta_desc || "Create posts, reels, videos and ads with AI. 1,600+ templates, one-click export. Part of TopRankPulse."} />
        <meta property="og:title" content={t.studio_landing_meta_title || "Studio — AI designs, reels & videos in 60 seconds | TopRankPulse"} />
        <meta property="og:description" content={t.studio_landing_meta_desc || "Create posts, reels, videos and ads with AI. 1,600+ templates, one-click export."} />
        <meta property="og:image" content="https://toprankpulse.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://toprankpulse.com/og-image.png" />
      </Helmet>
      {/* Hero */}
      <section className="ads-hero">
        <div className="ads-container ads-hero-grid">
          <div className="ads-hero-left">
            <span className="ads-eyebrow" data-testid="ads-hero-eyebrow">{t.studio_landing_eyebrow || "NEW · STUDIO BY TOPRANKPULSE"}</span>
            <h1 className="ads-h1" data-testid="ads-hero-title">
              {t.studio_landing_hero_title_a || "Create posts, reels and videos."}<br />{t.studio_landing_hero_title_b || "In minutes, not days."}
            </h1>
            <p className="ads-lead" data-testid="ads-hero-lead">
              {t.studio_landing_hero_lead || "Pick a content type, a template, or let AI draft the first version. Edit in a Canva-style editor. Export PNG / MP4 in one click. Ads, reels, stories, videos and posts — all in one."}
            </p>
            <div className="ads-cta-row">
              <Link to="/studio/create" className="ads-btn ads-btn-primary" data-testid="ads-cta-instant-campaign">
                <Zap size={18} /> {t.studio_landing_cta_start || "Start creating"}
              </Link>
              <Link to="/ads-creator/instant-campaign" className="ads-btn ads-btn-ghost" data-testid="ads-cta-start-free">
                <Rocket size={18} /> {t.studio_landing_cta_instant || "Instant Campaign — 25 ads"}
              </Link>
              <Link to="/ads-creator/templates" className="ads-btn ads-btn-ghost" data-testid="ads-cta-templates">
                <Layers size={18} /> {t.studio_landing_cta_browse || "Browse templates"}
              </Link>
              <Link to="/ads-creator/photo-start" className="ads-btn ads-btn-ghost" data-testid="ads-cta-photo-start">
                <ImageIcon size={18} /> {t.studio_landing_cta_photo_start || "Start from Photo"}
              </Link>
            </div>

            <div style={{ marginTop: 28 }}>
              <ReelDirectorBar presetPrompt={presetPrompt} presetMode="multi" />
            </div>
            {stats && (
              <div className="ads-kpi-row" data-testid="ads-kpis">
                <div className="ads-kpi"><div className="ads-kpi-value">{stats.projects_created.toLocaleString()}</div><div className="ads-kpi-label">{t.studio_landing_kpi_projects || "Projects created"}</div></div>
                <div className="ads-kpi"><div className="ads-kpi-value">{stats.exports_downloaded.toLocaleString()}</div><div className="ads-kpi-label">{t.studio_landing_kpi_exports || "Exports downloaded"}</div></div>
                <div className="ads-kpi"><div className="ads-kpi-value">{stats.templates_available.toLocaleString()}+</div><div className="ads-kpi-label">{t.studio_landing_kpi_templates || "Templates"}</div></div>
                <div className="ads-kpi"><div className="ads-kpi-value">10</div><div className="ads-kpi-label">{t.studio_landing_kpi_formats || "Ad formats"}</div></div>
              </div>
            )}
          </div>
          <div className="ads-hero-right">
            <LivePreview />
          </div>
        </div>
      </section>

      {/* Before / After Section */}
      <BeforeAfterSection />

      {/* "Made by AI in 10 seconds" social-proof gallery */}
      <DirectorShowcase
        onTryPrompt={(sc) => {
          setPresetPrompt(sc.prompt);
          const bar = document.querySelector('[data-testid="ads-director-bar"]');
          if (bar) bar.scrollIntoView({ behavior: "smooth", block: "center" });
        }}
        t={t}
      />

      {/* Features */}
      <section className="ads-section">
        <div className="ads-container">
          <h2 className="ads-section-title">{t.studio_landing_features_title || "A studio. Not a slideshow."}</h2>
          <p className="ads-section-sub">{t.studio_landing_features_sub || "Everything a performance marketer actually uses — one screen, no tabs."}</p>
          <div className="ads-feature-grid" data-testid="ads-features">
            <Feat icon={<Wand2 size={18} />} title={t.studio_landing_feat_ai_title || "AI Magic from URL"} text={t.studio_landing_feat_ai_text || "Paste a website, get 5 full ad concepts — brand colors, headlines, CTAs, ready-to-ship canvases."} />
            <Feat icon={<Type size={18} />} title={t.studio_landing_feat_type_title || "Full typography"} text={t.studio_landing_feat_type_text || "Fonts, weights, tracking, outlines, drop shadows. Textboxes that behave like Adobe, not PowerPoint."} />
            <Feat icon={<ImageIcon size={18} />} title={t.studio_landing_feat_img_title || "AI + stock images"} text={t.studio_landing_feat_img_text || "Generate pixel-perfect backgrounds with GPT Image 1. Unsplash and Pexels baked in."} />
            <Feat icon={<Palette size={18} />} title={t.studio_landing_feat_brand_title || "Brand kit"} text={t.studio_landing_feat_brand_text || "Lock your colors, fonts, logos once. Every new ad inherits them automatically."} />
            <Feat icon={<Layers size={18} />} title={t.studio_landing_feat_resize_title || "Smart resize"} text={t.studio_landing_feat_resize_text || "One canvas → 10 sibling ads auto-scaled to every format. Ship a full campaign in one click."} />
            <Feat icon={<Globe size={18} />} title={t.studio_landing_feat_wl_title || "White-label ready"} text={t.studio_landing_feat_wl_text || "Built to be embedded. Drop into your agency portal via iframe or API. Your brand, our engine."} />
          </div>
        </div>
      </section>

      {/* Formats */}
      <section className="ads-section" style={{ background: "var(--ads-surface)" }}>
        <div className="ads-container">
          <h2 className="ads-section-title">{t.studio_landing_formats_title || "10 ad formats. One canvas."}</h2>
          <p className="ads-section-sub">{t.studio_landing_formats_sub || "Facebook, Instagram, Google Display, LinkedIn. Feed, Story, Reel."}</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px" }} data-testid="ads-formats">
            {FORMATS.map((f) => (
              <div key={f.id} className="ads-feature" style={{ textAlign: "center", padding: "16px 10px" }}>
                <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: "-0.02em" }}>{f.label}</div>
                <div className="ads-feature-text" style={{ marginTop: 4 }}>{f.w}×{f.h}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="ads-section">
        <div className="ads-container">
          <h2 className="ads-section-title">{t.studio_landing_pricing_title || "Simple, credit-based pricing."}</h2>
          <p className="ads-section-sub">{t.studio_landing_pricing_sub || "Start free. Scale when your campaigns do."}</p>
          <div className="ads-pricing-grid" data-testid="ads-pricing">
            <Price name="Free" price="€0" credits={t.studio_landing_price_free_credits || "3 ads · one-time"}
              features={[t.studio_landing_price_free_f1 || "All 10 formats", t.studio_landing_price_free_f2 || "AI Magic from URL", t.studio_landing_price_free_f3 || "PNG export", t.studio_landing_price_free_f4 || "No credit card"]}
              cta={t.studio_landing_price_free_cta || "Start free"} ctaLink="/ads-creator/editor" />
            <Price name="Starter" price="€29/mo" credits={t.studio_landing_price_starter_credits || "10 ads / month"}
              features={[t.studio_landing_price_starter_f1 || "Everything in Free", t.studio_landing_price_starter_f2 || "Brand Kit", t.studio_landing_price_starter_f3 || "Smart Resize", t.studio_landing_price_starter_f4 || "Priority queue"]}
              cta={t.studio_landing_price_starter_cta || "Get Starter"} ctaLink="/login?plan=ads_creator_starter" packageId="ads_creator_starter" />
            <Price name="Pro" price="€69/mo" credits={t.studio_landing_price_pro_credits || "30 ads / month"} highlight badge={t.studio_landing_price_most_popular || "Most popular"}
              features={[t.studio_landing_price_pro_f1 || "Everything in Starter", t.studio_landing_price_pro_f2 || "AI Image Gen (GPT Image 1)", t.studio_landing_price_pro_f3 || "Video export (coming)", t.studio_landing_price_pro_f4 || "Remove watermark"]}
              cta={t.studio_landing_price_pro_cta || "Get Pro"} ctaLink="/login?plan=addon_ads_creator" packageId="addon_ads_creator" />
            <Price name="Agency" price="€149/mo" credits={t.studio_landing_price_agency_credits || "80 ads / month"}
              features={[t.studio_landing_price_agency_f1 || "Everything in Pro", t.studio_landing_price_agency_f2 || "Team seats (coming)", t.studio_landing_price_agency_f3 || "White-label export", t.studio_landing_price_agency_f4 || "Dedicated support"]}
              cta={t.studio_landing_price_agency_cta || "Get Agency"} ctaLink="/login?plan=ads_creator_agency" packageId="ads_creator_agency" />
          </div>
          {packages.length > 0 && (
            <div style={{ textAlign: "center", color: "var(--ads-text-dim)", marginTop: 18, fontSize: 13 }}>
              {t.studio_landing_pricing_extra || "Extra ads beyond plan: €2.30 / credit · cancel any time"}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="ads-section" style={{ background: "linear-gradient(120deg, rgba(168,85,247,0.15), rgba(124,58,237,0.15))" }}>
        <div className="ads-container" style={{ textAlign: "center" }}>
          <h2 className="ads-section-title" style={{ textAlign: "center" }}>{t.studio_landing_final_title || "Ship your first ad tonight."}</h2>
          <p className="ads-section-sub">{t.studio_landing_final_sub || "Three free ads. No card. Two clicks to publish."}</p>
          <Link to="/ads-creator/editor" className="ads-btn ads-btn-primary" data-testid="ads-cta-final">
            <Sparkles size={18} /> {t.studio_landing_final_btn || "Open the editor"}
          </Link>
        </div>
      </section>

      {/* Mini footer */}
      <footer style={{ borderTop: "1px solid var(--ads-border)", padding: "24px 28px", color: "var(--ads-text-dim)", fontSize: 13, textAlign: "center" }}>
        {t.studio_landing_footer || "Studio · part of"} <Link to="/" style={{ color: "var(--ads-text)" }}>TopRankPulse</Link>
      </footer>
      <AchievementCelebration />
    </div>
    </StudioShell>
  );
}

const FORMATS = [
  { id: "fb", label: "FB Feed", w: 1200, h: 628 },
  { id: "ig", label: "IG Feed", w: 1080, h: 1080 },
  { id: "reel", label: "Reel/Story", w: 1080, h: 1920 },
  { id: "fbs", label: "FB Story", w: 1080, h: 1920 },
  { id: "gd", label: "Google", w: 300, h: 250 },
  { id: "li", label: "LinkedIn", w: 1584, h: 396 },
];

function Feat({ icon, title, text }) {
  return (
    <div className="ads-feature">
      <div className="ads-feature-icon">{icon}</div>
      <div className="ads-feature-title">{title}</div>
      <div className="ads-feature-text">{text}</div>
    </div>
  );
}

function Price({ name, price, credits, features, cta, ctaLink, highlight, badge, packageId }) {
  return (
    <div className={`ads-price ${highlight ? "highlight" : ""}`} data-testid={`ads-price-${name.toLowerCase()}`}>
      {badge && <div className="ads-price-badge">{badge}</div>}
      <div className="ads-price-name">{name}</div>
      <div className="ads-price-amount">{price}</div>
      <div style={{ color: "var(--ads-text-dim)", fontSize: 13 }}>{credits}</div>
      <ul className="ads-price-features">
        {features.map((f, i) => (<li key={i}><Check size={14} /> {f}</li>))}
      </ul>
      <Link to={ctaLink} className="ads-btn ads-btn-primary" style={{ width: "100%", justifyContent: "center" }} data-testid={`ads-price-cta-${packageId || "free"}`}>
        <Zap size={16} /> {cta}
      </Link>
    </div>
  );
}
